import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, API, getToken } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Newspaper, Download, Trash2, Loader2, FileText, Lightbulb, ChevronLeft, ExternalLink, Sparkles } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { de } from "date-fns/locale";

const PROPOSAL_META = {
  social_post:  { label: "Social-Post",   icon: Sparkles },
  landing_page: { label: "Landing Page",  icon: FileText },
  product_idea: { label: "Produktidee",   icon: Lightbulb },
};

export default function Berichte() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setReports((await api.get("/research/reports")).data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Bericht löschen?")) return;
    try { await api.delete(`/research/reports/${id}`); toast.success("Gelöscht"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const download = (id, title) => {
    const token = getToken();
    const url = `${API}/research/reports/${id}/download`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `bericht-${(title || id).slice(0, 40)}.md`;
        a.click(); URL.revokeObjectURL(u);
      });
  };

  const apply = async (reportId, proposalId) => {
    try {
      const { data } = await api.post(`/research/reports/${reportId}/proposals/${proposalId}/apply-as-draft`);
      toast.success(`Vorschlag als ${data.tool}-Entwurf vorbereitet – jetzt im KI-Agent-Chat zur Freigabe`);
      load();
      if (open?.id === reportId) {
        const fresh = (await api.get(`/research/reports/${reportId}`)).data;
        setOpen(fresh);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="berichte-page">
      <header className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Research</div>
        <h1 className="text-3xl font-extrabold tracking-tight text-white mt-1">Berichte & Ideen</h1>
        <p className="text-sm text-slate-400 mt-2">
          Rechercheergebnisse (manuell im Chat oder vom wöchentlichen Skill erstellt). Jeder Vorschlag läuft durch die normale Freigabe im KI-Agenten.
        </p>
      </header>

      <Card className="bg-[#111827] border-white/5">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-slate-400 text-center">Lade…</div>
          ) : reports.length === 0 ? (
            <div className="p-16 text-center" data-testid="berichte-empty">
              <Newspaper className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <div className="text-white font-semibold mb-1">Noch keine Berichte</div>
              <div className="text-sm text-slate-400">Wenn der KI-Agent oder ein Skill eine Recherche abschließt, erscheint sie hier.</div>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {reports.map((r) => (
                <button key={r.id} onClick={() => setOpen(r)}
                  data-testid={`bericht-row-${r.id}`}
                  className="w-full text-left p-4 hover:bg-white/[0.02] transition flex items-start gap-3">
                  <Newspaper className="h-5 w-5 text-emerald-400 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium">{r.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {r.created_by} · {formatDistanceToNow(parseISO(r.created_at), { addSuffix: true, locale: de })}
                      {" · "}{(r.sources || []).length} Quellen · {(r.proposals || []).length} Vorschläge
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bericht-detail-dialog">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle>{open.title}</DialogTitle>
                <DialogDescription className="text-slate-500">
                  {open.created_by} · erstellt {formatDistanceToNow(parseISO(open.created_at), { addSuffix: true, locale: de })}
                </DialogDescription>
              </DialogHeader>
              {open.goal && (
                <div className="text-xs text-slate-400 italic">Ziel: {open.goal}</div>
              )}
              <article className="prose prose-invert max-w-none text-sm text-slate-200 whitespace-pre-wrap" data-testid="bericht-content">
                {open.content}
              </article>

              {(open.sources || []).length > 0 && (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Quellen</div>
                  <ul className="space-y-1 text-sm" data-testid="bericht-sources">
                    {open.sources.map((s, i) => (
                      <li key={i}>
                        <a href={s.url} target="_blank" rel="noreferrer"
                          className="text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1 text-xs">
                          <ExternalLink className="h-3 w-3" /> {s.title || s.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(open.proposals || []).length > 0 && (
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mb-2">Vorschläge</div>
                  <div className="space-y-2" data-testid="bericht-proposals">
                    {open.proposals.map((p) => {
                      const meta = PROPOSAL_META[p.type] || { label: p.type, icon: Sparkles };
                      const Icon = meta.icon;
                      const taken = p.status === "übernommen";
                      return (
                        <div key={p.id} className="flex items-start gap-3 p-3 rounded border border-white/5 bg-white/[0.02]"
                          data-testid={`proposal-${p.id}`}>
                          <Icon className="h-4 w-4 text-emerald-400 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white">{p.title}</div>
                            <Badge className="bg-white/5 text-slate-400 border-white/10 border text-[10px] mt-1">{meta.label}</Badge>
                            {taken && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px] ml-1">übernommen</Badge>}
                          </div>
                          <Button size="sm" disabled={taken} onClick={() => apply(open.id, p.id)}
                            data-testid={`proposal-apply-${p.id}`}
                            className="bg-emerald-500 hover:bg-emerald-600 h-7 text-xs">
                            Als Entwurf übernehmen
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => download(open.id, open.title)}
                  data-testid="bericht-download"
                  className="border-white/10 bg-transparent text-slate-300 hover:bg-white/5">
                  <Download className="h-4 w-4 mr-2" /> .md herunterladen
                </Button>
                <Button variant="ghost" onClick={() => remove(open.id)} data-testid="bericht-delete"
                  className="text-red-400 hover:text-red-300">
                  <Trash2 className="h-4 w-4 mr-1" /> Löschen
                </Button>
                <Button variant="ghost" onClick={() => setOpen(null)}>Schließen</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
